// Shared free-tier usage-gauge logic — the single source for the 3-tier
// thresholds + colors used by the Account plan card and the Dashboard usage
// section. The cap itself is `limit` from /api/usage (backend FREE_CONTACTS_CAP);
// this file owns only the warn ratio + the tier→color mapping, so there is no
// hardcoded 80 or 100 anywhere. See REFERENCE/plans/PLAN_C8_AT_CAP_UI.md.

export const USAGE_WARN_RATIO = 0.8; // amber heads-up at 80% of the cap

export type UsageState = 'ok' | 'warn' | 'over';

export interface UsageTier {
  pct: number; // count / limit (can exceed 1 when over)
  percent: number; // 0..100, clamped — for the bar width + the "%" label
  state: UsageState;
  remaining: number; // max(0, limit - count)
}

export function usageTier(count: number, limit: number): UsageTier {
  const pct = limit > 0 ? count / limit : 0;
  const state: UsageState =
    pct >= 1 ? 'over' : pct >= USAGE_WARN_RATIO ? 'warn' : 'ok';
  return {
    pct,
    percent: Math.min(100, Math.round(pct * 100)),
    state,
    remaining: Math.max(0, limit - count),
  };
}

// Tailwind tokens per tier: blue (info) → amber → red. Coral stays reserved for
// CTAs, so the bar never competes with the Upgrade button.
export const USAGE_FILL: Record<UsageState, string> = {
  ok: 'bg-info',
  warn: 'bg-amber-500',
  over: 'bg-red-500',
};
export const USAGE_TRACK: Record<UsageState, string> = {
  ok: 'bg-dark/5',
  warn: 'bg-dark/5',
  over: 'bg-red-100',
};
export const USAGE_TEXT: Record<UsageState, string> = {
  ok: 'text-muted-foreground',
  warn: 'text-amber-700',
  over: 'text-red-600',
};
